import React, { useState, useEffect, useRef } from 'react';
import { Play, Sparkles, MapPin, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../ui/carousel";

export default function VideoGallery({ videos, title, sub }) {
  const [api, setApi] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!api || playingId) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    
    intervalRef.current = setInterval(() => {
      if (api.canScrollNext()) {
        api.scrollNext();
      } else {
        api.scrollTo(0);
      }
    }, 5000);

    return () => clearInterval(intervalRef.current);
  }, [api, playingId]);

  if (!videos || videos.length === 0) return null;

  const videoCount = videos.length;

  return (
    <section className="py-20 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand-orange/10 text-brand-orange rounded-full text-[12px] font-bold uppercase tracking-wider mb-3">
              <Sparkles size={14} /> Travel Diaries
            </div>
            <h2 className="font-poppins text-[26px] sm:text-[34px] lg:text-[46px] text-brand-blue font-extrabold leading-[1.1]">
              {title || <>Your Journey <span className="text-brand-orange text-gradient">Visualized</span></>}
            </h2>
            <p className="text-brand-blue/65 text-[15px] lg:text-[18px] mt-3 max-w-2xl leading-relaxed">
              {sub || "Actual glimpses from our travelers' journeys across the Himalayas. No filters, just raw beauty and authentic experiences."}
            </p>
          </div>
          <div className="hidden lg:flex items-center gap-4">
            <div className="text-right">
              <div className="text-brand-blue font-bold text-lg leading-none">{videoCount}+</div>
              <div className="text-brand-blue/40 font-medium text-[11px] uppercase tracking-wider">Videos</div>
            </div>
            <div className="h-8 w-px bg-brand-blue/10" />
            <a href="https://www.youtube.com/@mountainschaintravel/shorts" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-brand-orange font-semibold text-[13px] hover:gap-3 transition-all">
              View All <ArrowRight size={14}/>
            </a>
          </div>
        </div>

        <div className="relative group/carousel">
          <Carousel 
            setApi={setApi}
            opts={{
              align: "start",
              loop: true,
              dragFree: false, // Set to false for better snap behavior with thumbnails
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4 md:-ml-6">
              {videos.map((v, i) => (
                <CarouselItem key={i} className="pl-4 md:pl-6 basis-[85%] sm:basis-1/2 lg:basis-1/2 py-2">
                  <div className="flex flex-col group h-full">
                    <div className="relative rounded-2xl overflow-hidden shadow-premium aspect-video bg-brand-blue mb-4 border border-brand-greyDark group-hover:border-brand-orange/30 transition-all">
                      {playingId === v.id ? (
                        <iframe
                          src={`https://www.youtube.com/embed/${v.id}?autoplay=1&rel=0&modestbranding=1`}
                          title={v.title || "Travel Video"}
                          className="absolute inset-0 w-full h-full border-0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <div 
                          className="absolute inset-0 w-full h-full cursor-pointer"
                          onClick={() => setPlayingId(v.id)}
                        >
                          <img 
                            src={`https://img.youtube.com/vi/${v.id}/hqdefault.jpg`} 
                            alt={v.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-brand-blue/20 group-hover:bg-brand-blue/10 transition-colors" />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-16 h-16 rounded-full bg-white/95 text-brand-orange shadow-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                              <Play size={28} className="ml-1 fill-brand-orange" />
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="absolute inset-0 pointer-events-none border-[3px] border-transparent group-hover:border-brand-orange/20 transition-all rounded-2xl" />
                    </div>
                    
                    <div className="px-2 pb-2">
                      <div className="flex items-center gap-1.5 text-brand-orange font-bold text-[11px] uppercase tracking-[0.1em] mb-1.5">
                        <MapPin size={12} strokeWidth={2.5}/> {v.destination || 'Himalayas'}
                      </div>
                      <h4 className="font-poppins font-bold text-brand-blue text-[19px] leading-snug group-hover:text-brand-orange transition-colors line-clamp-1">{v.title}</h4>
                      {v.link && (
                        <Link to={v.link} className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-brand-blue/60 mt-3 hover:text-brand-orange transition-colors group/link">
                          Explore this trip <ArrowRight size={14} className="group-hover/link:translate-x-1 transition-transform"/>
                        </Link>
                      )}
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            
            <CarouselPrevious className="hidden sm:flex absolute -left-12 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full border border-brand-greyDark shadow-xl bg-white text-brand-blue hover:bg-brand-orange hover:text-white transition-all z-10" />
            <CarouselNext className="hidden sm:flex absolute -right-12 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full border border-brand-greyDark shadow-xl bg-white text-brand-blue hover:bg-brand-orange hover:text-white transition-all z-10" />
          </Carousel>

          <div className="flex sm:hidden justify-center gap-1.5 mt-6">
            {videos.slice(0, 5).map((_, i) => (
              <div key={i} className="w-1.5 h-1.5 rounded-full bg-brand-blue/10" />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
